import type { Viewer } from '@cesiumgs/cesium-analytics'
import { Color, Entity, HeightReference, ScreenSpaceEventType } from '@cesiumgs/cesium-analytics'
import { type DrawResult } from './DrawResult'
import DrawAction from './DrawAction'
import { type DrawOptions } from './DrawOptions'

/**
 * Cesium Tool for Draw Point
 * @example
 * ```typescript
 * let draw=new Draw(view);
 * draw.create(DrawType.Point,null);
 * draw.on(DrawEventType.CursorUpdate,evt=>{
 * });
 * ```
 */
export default class DrawPointAction extends DrawAction {
  /**
   * @internal
   */
  addEntity?: Entity
  /**
   * @internal
   */
  moveEntity?: Entity
  /**
   * @internal
   */
  updateEntityPosition(entity: Entity, positon: any): void {
    if (this.drawoptions?.useDefaultEntity && entity) {
      entity.position = positon
    }
  }

  /**
   * @internal
   */
  destroy(): void {
    this.clear()
    this.viewer.dataSources.remove(this.dataSource, true)
  }

  /**
   * @internal
   */
  clear(): void {
    this.removeListener()
    this.dataSource.entities.removeAll()
  }

  /**
   * @internal
   */
  vertexAddHandler(movement: any): void {
    const ray = this.viewer.camera.getPickRay(movement.position)
    if (ray) {
      const position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position) {
        this.drawResults?.postions.splice(0, 1, position)
      }
      this.addEvt.raiseEvent(this.drawResults as any)

      this.updateEntityPosition(this.addEntity as any, position)
    }
  }

  /**
   * @internal
   */
  cursorUpdateHandler(movement: any) {
    const ray = this.viewer.camera.getPickRay(movement.endPosition)
    if (ray) {
      const position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position) {
        const res: DrawResult = {
          postions: [position],
        }
        this.updateEvt.raiseEvent(res as any)
        this.updateEntityPosition(this.moveEntity as any, position)
      }
    }
  }

  /**
   * @internal
   */
  removeListener(): void {
    this.vertexAddHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.LEFT_CLICK,
    )
    this.cursorUpdateHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.MOUSE_MOVE,
    )
    this.vertexCompleteHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.RIGHT_CLICK,
    )
  }

  /**
   * @internal
   */
  vertexCompleteHandler(movement: any): void {
    this.viewer.trackedEntity = undefined
    this.dataSource.entities.remove(this.moveEntity as any)
    this.removeListener()
    this.complete()
  }

  /**
   * @internal
   */
  registerViewerEvent(): void {
    this.vertexAddHandlerInstance?.setInputAction(
      this.vertexAddHandler.bind(this),
      ScreenSpaceEventType.LEFT_CLICK,
    )
    this.cursorUpdateHandlerInstance?.setInputAction(
      this.cursorUpdateHandler.bind(this),
      ScreenSpaceEventType.MOUSE_MOVE,
    )
    this.vertexCompleteHandlerInstance?.setInputAction(
      this.vertexCompleteHandler.bind(this),
      ScreenSpaceEventType.RIGHT_CLICK,
    )
  }

  /**
   * @internal
   */
  constructor(viewer: Viewer, options?: DrawOptions) {
    super(viewer, options)
    this.type = 'point'
    if (this.drawoptions?.useDefaultEntity) {
      const addEntity
        = this.drawoptions?.defaultPointEntity
        || new Entity({

          point: {
            color: Color.fromCssColorString('#FFD040'),
            pixelSize: 8,
            outlineWidth: 1,
            outlineColor: Color.WHITE,
            heightReference: HeightReference.CLAMP_TO_GROUND,
          },
        })
      const moveEntity
        = this.drawoptions?.defaultMovePointEntity
        || new Entity({
          point: {
            color: Color.BLUE,
            pixelSize: 8,
            outlineWidth: 1,
            outlineColor: Color.WHITE,
          },
        })

      this.addEntity = this.dataSource.entities.add(addEntity)
      this.moveEntity = this.dataSource.entities.add(moveEntity)
      this.addEntity = addEntity
    }
  }
}
